import useBreakpoint from 'hooks/useBreakpoint';
import ProfileSummary from 'components/MyAccount/ProfileSummary';
import { UserContext } from 'providers/UserProvider';
import { useEffect, useContext, useState } from 'react';
import Link from 'components/Globals/Base/Link';
import { MyAccountLinks } from 'constants/constants';
import { MY_ACCOUNT_MENU_TEST_IDS } from 'constants/testsIds/profileSection';
import {
  Container,
  Title,
  MenuItemWrapper,
  MenuItemsWrapper,
  CustomerSupportWrapper,
  MenuItemsCustomerSupport,
} from './MyAccountNavigationStyles';

export interface MyAccountNavigationProps {
  activeLink: string;
  account: {
    pianoAccessName?: string;
    firstName?: string;
    lastName?: string;
    subscriptionInfo?: { status: string; daysLeft: number };
    accesses: string[];
  };
  newslettersCount: number | null | undefined;
}

const MyAccountNavigation = ({ activeLink, account, newslettersCount }: MyAccountNavigationProps) => {
  const {
    userState: { email: userEmail, isLoggedIn },
  } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useBreakpoint('mdMax');
  const profileSummaryProps = {
    ...account,
    className: 'my-account-mobile',
    isLoading,
    newslettersCount,
  };

  useEffect(() => {
    if (isLoggedIn && userEmail && isMobile && newslettersCount !== null) {
      setIsLoading(false);
    }
  }, [isLoggedIn, userEmail, isMobile, newslettersCount]);

  return (
    <Container>
      <Title
        as='h1'
        $fontFamily='graphikCond'
        $size={{ default: 'level4', mdMin: 'level1' }}
        data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.MY_ACCOUNT_TITLE}`}
      >
        My Account
      </Title>
      <MenuItemsCustomerSupport data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.MENU_ITEMS_CUSTOMER_SUPPORT}`}>
        <MenuItemsWrapper data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.MENU_ITEMS_WRAPPER}`}>
          {MyAccountLinks.map((item) => (
            <MenuItemWrapper
              key={item.label}
              $selected={activeLink === item.url}
              data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.MENU_ITEM_WRAPPER}`}
            >
              <Link
                href={item.url}
                aria-label={`Navigate to ${item.label} section of My Account`}
                aria-current={activeLink === item.url ? 'page' : undefined}
                className={item.label === 'Profile' ? 'profile accountNavigationItem' : 'accountNavigationItem'}
                type='tertiaryBlackButton'
                hasRightArrow={false}
                data-cy={`my-account-${item.label.toLowerCase().replace(' ', '-')}-navlink`}
              >
                {item.label}
              </Link>
              {item.url === '/my-account/my-profile/' && isMobile && (
                <ProfileSummary profileSummary={profileSummaryProps} />
              )}
            </MenuItemWrapper>
          ))}
        </MenuItemsWrapper>
        <CustomerSupportWrapper data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.CUSTOMER_SUPPORT_WRAPPER}`}>
          <p data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.CUSTOMER_SUPPORT_TITLE}`}>Customer Support</p>
          <span data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.CUSTOMER_SUPPORT_TEXT}`}>
            We’re here to help. Please click{' '}
            <Link
              href='/customerservice/'
              target='_blank'
              className='internal-page-link'
              type='blueSecondary'
              data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.CUSTOMER_SUPPORT_LINK}`}
            >
              here
            </Link>{' '}
            for answers to our most common questions. To contact our Support team, please message us directly at{' '}
            <Link
              href='mailto:support@fortune.com'
              target='_blank'
              className='internal-page-link'
              type='blueSecondary'
              data-cy={`${MY_ACCOUNT_MENU_TEST_IDS.CUSTOMER_SUPPORT_LINK}`}
            >
              support@fortune.com
            </Link>{' '}
            using the email address associated with your Fortune account.
          </span>
        </CustomerSupportWrapper>
      </MenuItemsCustomerSupport>
    </Container>
  );
};

export default MyAccountNavigation;
